import {
  AddIcon,
  Button,
  ButtonGroup,
  Card,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  EditIcon,
  Modal,
  Row,
  RowSelectionState,
  Table,
  TableColumns,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../hooks/useColumnsSort';
import { useMemorizedCacheTag } from '../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useCreateTimeGridEntryMutation, useCreateTimeGridMutation } from '../../types/planung-graphql-client-defs';
import { showSuccessDuplicateToast, showUserErrorToast } from '../../utils/toast';
import { TimeGridForm } from './Forms/TimeGridForm';
import { TimeGridType } from './graphql/types';
import styles from './TimeGrid.module.scss';

type TimeGidListProps = {
  data: TimeGridType[] | null;
  onSelect: (uuid: string) => void;
};

const TimeGidList: FC<TimeGidListProps> = ({ data, onSelect }) => {
  const { t } = useTranslation();

  const { sorting, saveSorting } = useColumnsSort('time_grid');

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [timeGrid, setTimeGrid] = useState<null | TimeGridType>(null);
  const [selected, setSelected] = useState<RowSelectionState>({ 0: true });
  const [duplicating, setDuplicating] = useState<string | null>(null);

  const context = useMemorizedCacheTag('TIME_GRID');
  const [, createTimeGrid] = useCreateTimeGridMutation();
  const [, createTimeGridEntry] = useCreateTimeGridEntryMutation();

  const createColumns = (): TableColumns<TimeGridType>[] => {
    return [
      {
        header: t('timeGrid.name'),
        accessorKey: 'name',
        id: 'name',
      },
      {
        header: t('common.comment'),
        accessorKey: 'description',
        id: 'description',
      },
      {
        header: t('timeGrid.hours'),
        accessorFn: (originalRow) => {
          return originalRow.entries.filter((entry) => !entry.pause).length;
        },
        id: 'hours',
      },
      {
        header: t('timeGrid.pauses'),
        accessorFn: (originalRow) => {
          return originalRow.entries.filter((entry) => entry.pause).length;
        },
        id: 'pause',
      },
    ];
  };

  const tableColumns = useMemo(createColumns, []);

  const memoizedData = useMemo((): TimeGridType[] => {
    return data && data ? data : [];
  }, [data]);

  // Stundenraster pflegen nur Admin und Stundenplan Write (Plan Rollen und Rechte, E4).
  const canEdit = usePermissionChecker()?.canWriteTimeGrid() ?? false;

  const onAddClick = () => {
    setTimeGrid(null);
    setModalOpen(true);
  };

  const handleEdit = (row: Row<TimeGridType>) => {
    setTimeGrid(row.original ?? null);
    setModalOpen(true);
  };

  // Kopie mit allen Stunden und Pausen; die Reihenfolge vergibt das Backend anhand der Beginnzeiten.
  const handleDuplicate = async (source: TimeGridType) => {
    setDuplicating(source.uuid);
    const created = await createTimeGrid(
      {
        input: {
          name: t('timeGrid.copyDefaultName', { name: source.name }),
          description: source.description,
        },
      },
      context,
    );
    const timeGridUuid = created.data?.createTimeGrid.uuid;
    if (created.error || !timeGridUuid) {
      showUserErrorToast({ error: created.error });
      setDuplicating(null);
      return;
    }
    for (const entry of source.entries) {
      const result = await createTimeGridEntry(
        {
          input: {
            timeGridUuid,
            name: entry.name,
            shortName: entry.shortName,
            start: entry.start,
            end: entry.end,
            reliable: entry.reliable,
            pause: entry.pause,
            print: entry.print,
          },
        },
        context,
      );
      if (result.error) {
        showUserErrorToast({ error: result.error });
        setDuplicating(null);
        return;
      }
    }
    showSuccessDuplicateToast([source.name]);
    setDuplicating(null);
  };

  const createActionItems = (row: Row<TimeGridType>): DropdownMenuItem[] => [
    {
      label: t('common.duplicate'),
      disabled: duplicating !== null,
      onClick: () => handleDuplicate(row.original),
    },
    { type: 'ruler' },
    {
      label: t('common.delete'),
      type: 'default',
      color: 'error',
      // Löschen ist noch nicht angebunden (bisher ein gesperrter Knopf).
      disabled: true,
    },
  ];

  const closeModal = () => {
    setTimeGrid(null);
    setModalOpen(false);
  };

  return (
    <>
      <Card className={styles['grid-list']} contentPadding='none'>
        <div className={styles['grid-list-header']}>
          <div className={styles['title']}>{t('timeGrid.title', { count: 2 })}</div>
          {canEdit && (
            <Button className={styles['button']} hierarchy='tertiary' icon={<AddIcon />} onClick={onAddClick}>
              {t('common.add')}
            </Button>
          )}
        </div>
        <Table
          sorting={sorting}
          onSortingChange={saveSorting}
          columns={tableColumns}
          data={memoizedData}
          onRowClick={(event, row) => {
            setSelected({ [row.id]: true });
            onSelect(row.original.uuid);
          }}
          showSelected
          showRowHover
          rowSelection={selected}
          lastColWidth='80px'
          lastCol={(row) => {
            if (!canEdit) return null;
            return (
              <ButtonGroup>
                <Button
                  hierarchy='secondary'
                  type='button'
                  icon={<EditIcon className='small' />}
                  onClick={() => {
                    handleEdit(row);
                  }}
                />
                <Dropdown
                  noPadding
                  trigger={
                    <Button
                      hierarchy='secondary'
                      type='button'
                      isLoading={duplicating === row.original.uuid}
                      icon={<DotsHorizontalIcon className='small' />}
                    />
                  }
                >
                  <DropdownMenu data={createActionItems(row)} />
                </Dropdown>
              </ButtonGroup>
            );
          }}
        />
      </Card>
      <Modal
        isOpen={modalOpen}
        onRequestClose={closeModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        title={timeGrid ? (t('timeGrid.edit') as string) : (t('timeGrid.create') as string)}
      >
        <TimeGridForm timeGrid={timeGrid} handleClose={closeModal}></TimeGridForm>
      </Modal>
    </>
  );
};

export const TimeGridTable = observer(TimeGidList);
